// ===========================================
// Email sequence types — MIRRORED FROM THE BACKEND, FIELD BY FIELD
// ===========================================
//
// ⚠️ A RESPONSE TYPE HERE IS AN ASSERTION, NEVER A VALIDATION. `apiClient.get<T>()`
// is a claim about the server, not a check of it — a field that drifts from the
// backend is invisible to `tsc`, to the build, and to every test that mocks the
// API with the same wrong shape. The admin Reports pages rendered `$NaN` in
// production for exactly this reason (seven fields the backend never emitted).
//
// So every type below was read off the backend source, not inferred:
//   routes:     src/routes/emailSequence.routes.ts       (15 routes, not 12)
//   views:      src/services/emailSequences/sequence.service.ts  `toView`
//               src/services/emailSequences/enrolment.service.ts `toEnrolmentView`
//   validation: src/validators/emailSequence.validators.ts
//
// When you change one of those, change this file in the same commit — and
// expect the resulting compile errors rather than re-widening a type to silence
// them. That is the point.

// ===========================================
// ENVELOPE
// ===========================================

/**
 * ⚠️ EVERY response from this API is wrapped. There is no bare array and no
 * `{ items: [...] }` anywhere in the router.
 *
 * `status` is the literal string `'success'` — NOT a boolean `success` field.
 *
 * The wrapper is the crash-prone part: `apiClient` already unwraps axios, so
 * declaring `apiClient.get<EmailSequence[]>(…)` type-checks green and then hands
 * you the ENVELOPE where you expected an array — `response.map is not a
 * function`, which blanks the page rather than rendering empty. Always go
 * through `ApiResponse<T>` and return `response.data`.
 */
export interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

/** `POST /:id/test` answers with a message and NO `data` key at all. */
export interface ApiMessageOnlyResponse {
  status: string;
  message: string;
}

// ===========================================
// ENUMS
// ===========================================

/** `START` = day 0 is the day you enrol. `DATE` = steps count back from a date. */
export type EmailSequenceAnchor = 'START' | 'DATE';

/** `COMPLETED` and `CANCELLED` are terminal; a returning student is a NEW enrolment. */
export type EmailSequenceEnrolmentStatus = 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';

/**
 * ⚠️ NOT BACKED BY A DATABASE ENUM. `EmailSequenceEnrolment.cancelReason` is
 * `String? @db.VarChar(40)`, and the view copies it straight out without
 * narrowing — so the API's honest type is `string | null` (see
 * `EmailSequenceEnrolment.cancelReason` below), and this union is a
 * DISPLAY-MAPPING KEY, not a guarantee.
 *
 * Treat it accordingly: look the reason up with a fallback, never an exhaustive
 * `switch` without a default. The service comment says the list widens, and a
 * sixth reason must degrade to readable text rather than render `undefined`.
 */
export type EmailSequenceCancelReason =
  | 'ADMIN'
  | 'STUDENT_INACTIVE'
  | 'STUDENT_DELETED'
  | 'NO_RECIPIENTS'
  | 'NO_CONTACTS';

/**
 * Per-step ledger state. `null` = no ledger row yet (not started).
 *
 * ⚠️ `CLAIMED` is NOT terminal — it means the sweep took the claim and then
 * died before recording an outcome. It counts as pending, and the backend's
 * stuck-claim warner is what surfaces it.
 */
export type EmailSequenceStepState = 'SENT' | 'FAILED' | 'SKIPPED' | 'CLAIMED' | null;

// ===========================================
// MERGE FIELDS
// ===========================================

/**
 * The four canonical fields, mirroring `EMAIL_SEQUENCE_MERGE_FIELDS`.
 *
 * ⚠️ `studentName` is available here and deliberately NOT in the custom-email
 * list: a sequence enrolment is per student, while a campaign targets contacts
 * who may have several children. Do not reuse the custom-email array.
 */
export const SEQUENCE_MERGE_FIELDS = [
  'firstName',
  'lastName',
  'schoolName',
  'studentName',
] as const;

export type SequenceMergeField = (typeof SEQUENCE_MERGE_FIELDS)[number];

/**
 * Accepted aliases. The backend's map is module-private, so this is a mirrored
 * copy — the validator accepts these, but every error message names only the
 * four canonical fields above.
 */
export const SEQUENCE_MERGE_FIELD_ALIASES: Record<string, SequenceMergeField> = {
  parentName: 'firstName',
  recipientName: 'firstName',
  childName: 'studentName',
};

// ===========================================
// SEQUENCE (the template)
// ===========================================

/** A template step. `sequenceId`/`schoolId`/timestamps are deliberately not exposed. */
export interface EmailSequenceStep {
  id: string;
  /** 0-based. Reassigned by array index on save — client values are ignored. */
  position: number;
  /** SIGNED, -365..365. Negative is legal ONLY when the sequence is DATE-anchored. */
  offsetDays: number;
  subject: string;
  preheader: string | null;
  /** Sanitised HTML. */
  content: string;
}

export interface EmailSequence {
  id: string;
  name: string;
  description: string | null;
  anchor: EmailSequenceAnchor;
  /** 6–21 inclusive. Not 0–23 — hour 2 is ambiguous across a DST boundary. */
  sendHour: number;
  isActive: boolean;
  /** ISO timestamp. Non-null means archived: read-only, and edits 409. */
  archivedAt: string | null;
  steps: EmailSequenceStep[];
  /** ACTIVE + PAUSED. Non-zero BLOCKS deletion. */
  liveEnrolmentCount: number;
  /** Every enrolment ever. Non-zero forces archive-instead-of-delete. */
  totalEnrolmentCount: number;
  createdAt: string;
  updatedAt: string;
}

// ===========================================
// ENROLMENT
// ===========================================

/**
 * One step of a live enrolment: the SNAPSHOT step merged with its ledger row.
 *
 * ⚠️ `preheader` and `content` are NOT exposed on the snapshot by any endpoint —
 * do not add them here. What a family actually received cannot be re-read.
 */
export interface EmailSequenceEnrolmentStep {
  /** The snapshot step id — stable from enrolment, not the template step's id. */
  id: string;
  position: number;
  offsetDays: number;
  subject: string;
  /** `@db.Date` — a UTC-midnight school-local calendar day. */
  dueDay: string;
  /**
   * ⚠️ SHOW THIS, NOT `dueDay`. With a backlog only one step sends per
   * school-day, so `dueDay` is a promise the sweep does not keep.
   *
   * ⚠️ MIXED SHAPE BY DESIGN: a UTC-midnight calendar day while pending, but a
   * FULL TIMESTAMP once sent (it falls back to the ledger's `sentAt`). Format it
   * by branching on `state`, never with one formatter for both.
   */
  sendDay: string;
  state: EmailSequenceStepState;
  sentAt: string | null;
  failedAt: string | null;
  failureReason: string | null;
}

export interface EmailSequenceEnrolmentCounts {
  total: number;
  sent: number;
  failed: number;
  skipped: number;
  /** No ledger row yet, OR claimed-but-unresolved. */
  pending: number;
}

export interface EmailSequenceEnrolment {
  id: string;
  sequenceId: string;
  /** Flattened from the sequence — there is no nested relation object. */
  sequenceName: string;
  studentId: string;
  /** Flattened `${firstName} ${lastName}`.trim(). */
  studentName: string;
  status: EmailSequenceEnrolmentStatus;
  anchor: EmailSequenceAnchor;
  /** `@db.Date` — UTC midnight. */
  startDay: string;
  /** `@db.Date` — UTC midnight. Null unless the sequence is DATE-anchored. */
  anchorDate: string | null;
  /** Copied from the sequence AT START — editing the sequence never moves it. */
  sendHour: number;
  /**
   * ⚠️ `string`, not `EmailSequenceCancelReason`. The column is a VarChar and
   * the backend does not narrow it. Map it for display with a fallback.
   */
  cancelReason: string | null;
  pausedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  steps: EmailSequenceEnrolmentStep[];
  counts: EmailSequenceEnrolmentCounts;
  createdAt: string;
}

// ===========================================
// PREVIEW
// ===========================================

/**
 * `GET /:id/preview/:position`.
 *
 * ⚠️ EXACTLY TWO FIELDS. The renderer returns `{ html, text, subject, branding }`
 * and the route destructures only these two — `text` and `branding` never reach
 * the client.
 */
export interface EmailSequenceStepPreview {
  html: string;
  subject: string;
}

/** One projected step in a start preview. ⚠️ No `id` and no `subject`. */
export interface EmailSequenceStartPreviewStep {
  position: number;
  offsetDays: number;
  /** `@db.Date` — UTC midnight. */
  dueDay: string;
  /**
   * `@db.Date` — UTC midnight. The day it will ACTUALLY go out, or `null` when
   * this step will be SKIPPED and so never sends.
   *
   * ⚠️ Nullable since /qa 2026-09-17. It was projected over every step
   * regardless of the skip choice, which showed the admin dates the sweep would
   * never honour — measured on an exam series, the last email was shown landing
   * AFTER the exam when it actually sends the day before.
   */
  sendDay: string | null;
  pastDue: boolean;
  /** Pre-claimed as SKIPPED at start, so it can never send. */
  skipped: boolean;
}

/**
 * A recipient on the start preview.
 *
 * ⚠️ `email` IS PERMISSION-GATED AND THE KEY IS ABSENT, NOT NULL, for a
 * read-only admin. Declaring it required would type-check green and render
 * `undefined` for exactly those users.
 */
export interface EmailSequenceStartPreviewRecipient {
  name: string;
  email?: string;
}

export interface EmailSequenceStartPreview {
  steps: EmailSequenceStartPreviewStep[];
  pastDueCount: number;
  /**
   * DATE-anchored only: would the backlog push a step PAST the anchor date.
   * Always false for START-anchored. When true, the skip-past-due choice is not
   * really a choice — un-ticking it sends a "two days before the exam" email
   * after the exam.
   */
  overrun: boolean;
  recipientCount: number;
  recipients: EmailSequenceStartPreviewRecipient[];
}

// ===========================================
// STATUS
// ===========================================

/**
 * `GET /status`.
 *
 * ⚠️ Consumed as `ApiResponse<EmailSequenceStatus>` — the boolean is at
 * `data.enabled`, NOT at the root. Reading `response.enabled` yields
 * `undefined`, which is falsy, so the "switched off" banner would render
 * PERMANENTLY in both flag states with `tsc` green.
 */
export interface EmailSequenceStatus {
  enabled: boolean;
}

// ===========================================
// REQUEST BODIES
// ===========================================

export interface EmailSequenceStepInput {
  offsetDays: number;
  subject: string;
  preheader?: string | null;
  content: string;
}

export interface CreateEmailSequenceInput {
  name: string;
  description?: string | null;
  anchor?: EmailSequenceAnchor;
  sendHour?: number;
  isActive?: boolean;
  steps: EmailSequenceStepInput[];
}

/**
 * ⚠️ Sending `steps` REPLACES them wholesale; positions are reassigned by array
 * index. There is no per-step PATCH.
 */
export type UpdateEmailSequenceInput = Partial<CreateEmailSequenceInput>;

export interface StartEmailSequenceInput {
  studentId: string;
  /**
   * A bare `YYYY-MM-DD` calendar day. ⚠️ Never `toISOString()` — an evening
   * Sydney instant is already tomorrow in UTC and shifts the whole series.
   * Required by the SERVICE whenever the sequence is DATE-anchored, even though
   * the schema marks it optional.
   */
  anchorDate?: string;
  /** Defaults to `true` server-side. */
  skipPastDue?: boolean;
}

/**
 * ⚠️ CARRIES `skipPastDue`, unlike its earlier shape. The preview must be taken
 * under the SAME assumption as the start, or it projects send-days the sweep
 * will not follow.
 */
export type StartEmailSequencePreviewInput = StartEmailSequenceInput;

export interface TestEmailSequenceInput {
  /** 0–29. Sends to the logged-in admin only — there is no `email` field. */
  position: number;
  studentId?: string;
}

/** `DELETE /:id` — `data` is this, not the sequence and not null. */
export interface EmailSequenceDeleteResult {
  archived: boolean;
}

// ===========================================
// VALIDATION LIMITS (mirrored — keep in step with the backend validators)
// ===========================================

export const SEQUENCE_LIMITS = {
  nameMax: 120,
  descriptionMax: 500,
  subjectMax: 200,
  preheaderMax: 150,
  /** 256KB per step. 30 steps × 256KB ≈ 7.7MB against Express's 10MB cap. */
  contentMax: 256_000,
  stepsMin: 1,
  stepsMax: 30,
  offsetMin: -365,
  offsetMax: 365,
  sendHourMin: 6,
  sendHourMax: 21,
} as const;

/**
 * The 422 body when a step names a merge field that does not exist.
 *
 * ⚠️ ASYMMETRIC ON PURPOSE: `invalidMergeFields` entries ARRIVE WRAPPED in
 * `{{ }}`, `allowed` entries do NOT.
 *
 * ⚠️ Do not string-match these against your token list to find the offending
 * step — a formatting-split token arrives with a trailing
 * `' (broken by formatting — delete it and retype it)'`, so equality fails. The
 * payload names no step index at all, so inline attribution has to be computed
 * client-side; treat this response as a backstop.
 */
export interface MergeFieldErrorDetails {
  invalidMergeFields: string[];
  allowed: string[];
}
